import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import type { Supply48DataSource } from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { KOMA_COUNT } from '~/constants/koma'
import type { GateCloseFrontier } from '~/models/gateClose'
import { makeEditRowKey } from '~/pages/Supply48Data/models/editState'
import { isCellLocked } from '~/pages/Supply48Data/models/gcLock'
import type { DayIndex } from '~/pages/Supply48Data/models/saveRows'
import { isNonStandardSource } from '~/pages/Supply48Data/models/sourceKind'

/**
 * 非定型の取引の列 (差金決済タブの列は除く) の source id。
 * 値を 0 のまま手で入れた日を「入力済み」と区別する印の対象 (AC-S4D-060)。
 */
export function nonStandardLegIds(
  sources: readonly Supply48DataSource[],
  category: SupplyCategory,
): Set<string> {
  if (category === SupplyCategory.FUTURES) return new Set()
  return new Set(
    sources.filter(isNonStandardSource).map((s) => s.supply48SourceId),
  )
}

/**
 * 非定型の取引の入力漏れの注意 (AC-S4D-060) を出す列 (source) を返す。
 *
 * 次をすべて満たす受渡日が表示中にある列が対象:
 * - 非定型の取引の列 (差金決済タブを除く)
 * - **保存値**が全コマ 0 (編集中の値は見ない。未設定のコマは 0 として表示するので 0 扱い)
 * - まだ GC が来ていないコマが 1 つ以上ある。受渡期間の外の日は全コマ編集不可
 *   (`isCellLocked`) なので、過去日・ロック済みの日と同じく対象外
 * - 保存済みの手動変更の印が 1 つも無い (0 を手で入れて保存した日は対象外)
 *
 * 行の無い日は「全コマ 0 の保存値」が無いので対象にしない。
 */
export function findInputMissingSources(
  sources: readonly Supply48DataSource[],
  category: SupplyCategory,
  visibleDates: readonly ProtoDate[],
  dayIndex: DayIndex,
  gateClose: GateCloseFrontier | undefined,
): Supply48DataSource[] {
  const targetIds = nonStandardLegIds(sources, category)
  return sources.filter((source) => {
    if (!targetIds.has(source.supply48SourceId)) return false
    return visibleDates.some((date) => {
      const day = dayIndex.get(
        makeEditRowKey(
          source.supply48SourceId,
          date.year,
          date.month,
          date.day,
        ),
      )
      if (day === undefined) return false
      if (day.manualEditKomas.length > 0) return false
      for (let i = 0; i < KOMA_COUNT; i += 1) {
        if ((day.slots[i]?.value ?? 0) !== 0) return false
      }
      for (let koma = 1; koma <= KOMA_COUNT; koma += 1) {
        if (!isCellLocked(gateClose, day, date, koma)) return true
      }
      return false
    })
  })
}
